import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

// Cache for order counts to avoid repeated fetches
const orderCountsCache: Record<string, number> = {};
let cacheLoaded = false;
let loadingPromise: Promise<void> | null = null;

// Threshold for bestseller badge
export const BESTSELLER_THRESHOLD = 5;

export const useProductOrderCount = (productId: string) => {
  const [orderCount, setOrderCount] = useState<number | null>(
    orderCountsCache[productId] ?? null
  );
  const [loading, setLoading] = useState(!cacheLoaded);

  useEffect(() => {
    const loadCounts = async () => {
      // If already cached for this product, use it
      if (orderCountsCache[productId] !== undefined) {
        setOrderCount(orderCountsCache[productId]);
        setLoading(false);
        return;
      }

      // If cache is already loaded but product not found, it means 0
      if (cacheLoaded) {
        setOrderCount(0);
        setLoading(false);
        return;
      }

      // Load all counts at once from materialized view (batch)
      if (!loadingPromise) {
        loadingPromise = (async () => {
          try {
            // Try to fetch from materialized view first (faster)
            const { data, error } = await supabase
              .from('product_order_stats')
              .select('product_id, order_count');

            if (error) {
              // Fallback to manual query if view doesn't exist
              console.warn('Materialized view not found, falling back to manual query:', error.message);
              await loadCountsManually();
              return;
            }

            // Populate cache from materialized view
            (data || []).forEach((item: { product_id: string; order_count: number }) => {
              orderCountsCache[item.product_id] = item.order_count;
            });

            cacheLoaded = true;
          } catch (err) {
            console.error('Error loading order counts:', err);
            cacheLoaded = true;
          }
        })();
      }

      await loadingPromise;
      setOrderCount(orderCountsCache[productId] || 0);
      setLoading(false);
    };

    loadCounts();
  }, [productId]);

  const isBestseller = orderCount !== null && orderCount >= BESTSELLER_THRESHOLD;

  return { orderCount, loading, isBestseller };
};

// Fallback function for manual counting (if materialized view doesn't exist)
const loadCountsManually = async () => {
  try {
    // Get completed orders first
    const { data: completedOrders, error: ordersError } = await supabase
      .from('orders')
      .select('id')
      .eq('status', 'completed');

    if (ordersError || !completedOrders || completedOrders.length === 0) {
      cacheLoaded = true;
      return;
    }

    const orderIds = completedOrders.map(o => o.id);

    const { data, error } = await supabase
      .from('order_items')
      .select('product_id')
      .in('order_id', orderIds);

    if (error) {
      cacheLoaded = true;
      return;
    }

    // Aggregate counts by product_id
    (data || []).forEach((item: { product_id: string }) => {
      if (item.product_id) {
        orderCountsCache[item.product_id] = (orderCountsCache[item.product_id] || 0) + 1;
      }
    });

    cacheLoaded = true;
  } catch (err) {
    console.error('Error in manual count loading:', err);
    cacheLoaded = true;
  }
};

// Function to clear cache (useful when orders are updated)
export const clearOrderCountsCache = () => {
  Object.keys(orderCountsCache).forEach(key => delete orderCountsCache[key]);
  cacheLoaded = false;
  loadingPromise = null;
};
